import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db, ref, set } from "../Firebase/firebase-config";
import { createUserWithEmailAndPassword, fetchSignInMethodsForEmail } from "firebase/auth";
import { get, onValue, remove } from "firebase/database";
import { 
  getStorage, 
  ref as storageRef, 
  deleteObject, 
  listAll 
} from "firebase/storage";
import { 
  initializeApp as initializeSecondaryApp 
} from "firebase/app";
import { 
  getAuth as getSecondaryAuth, 
} from "firebase/auth";
import { getAuth, signInWithEmailAndPassword, deleteUser } from "firebase/auth";
import { firebaseConfig } from "../Firebase/firebase-config"; // Assuming firebaseConfig is exported from firebase-config.js
import { useAccount } from "../context/AccountContext";
import { FiArrowLeft, FiCheckCircle, FiAlertCircle, FiLoader, FiTrash2 } from "react-icons/fi";
import { FaInfoCircle, } from "react-icons/fa";
import "./AddFirms.css";

export default function AddFirm() {
  const { currentAccount, reload } = useAccount();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    username: "",
    name: "",
    phone: "",
    email: "",
    gst: "",
    pan: "",
  });

  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState(""); // success | error
  const [showInfo, setShowInfo] = useState(false);

  // Fetch existing firms
  useEffect(() => {
    if (currentAccount?.type !== "main") return;

    const firmsRef = ref(db, `profileDashboard/${currentAccount.uid}/firms`);
    const unsubscribe = onValue(firmsRef, async (snap) => {
      const data = snap.val() || {};
      const firmUids = Object.keys(data);
      const firmPromises = firmUids.map(async (uid) => {
        const profileSnap = await get(ref(db, `profileDashboard/${uid}`));
        const profile = profileSnap.val();
        if (!profile) return null;
        return {
          uid,
          pwd: data[uid]?.pwd || "", // Get stored password
          ...profile,
        };
      });
      const fullFirms = (await Promise.all(firmPromises)).filter(Boolean);
      setFirms(fullFirms);
    });

    return () => unsubscribe();
  }, [currentAccount]);

  // Auto-fill PAN from GST
  const handleGST = (gstValue) => {
    const cleanGST = gstValue.toUpperCase().trim();
    if (cleanGST.length >= 12) {
      const autoPAN = cleanGST.substring(2, 12);
      setForm({ ...form, gst: cleanGST, pan: autoPAN });
    } else {
      setForm({ ...form, gst: cleanGST, pan: "" });
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();

  if (currentAccount?.type !== "main") {
    setMsg("Only the main account can create firms.");
    setMsgType("error");
    return;
  }

  setMsg("");
  setLoading(true);

  try {
    // 1. Validate phone
    const cleanPhone = form.phone.replace(/[^\d]/g, "");
    if (cleanPhone.length !== 10) {
      throw new Error("Enter a valid 10-digit phone number");
    }

    // ────────────────────────────────────────────────
    //  2. Check username uniqueness   ←←←  ADD HERE
    // ────────────────────────────────────────────────
    const usernameSnap = await get(ref(db, `profileDashboard`));
    const existingUsernames = Object.values(usernameSnap.val() || {})
      .filter(u => u?.username)                     // only entries that have username
      .map(u => u.username.trim().toLowerCase());   // normalize (recommended)

    const newUsernameNormalized = form.username.trim().toLowerCase();

    if (existingUsernames.includes(newUsernameNormalized)) {
      throw new Error("This username is already taken. Please choose another.");
    }

    // ────────────────────────────────────────────────
    // 3. Validate & check real email
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      throw new Error("Please enter a valid email address");
    }

    const emailMethods = await fetchSignInMethodsForEmail(auth, form.email);
    if (emailMethods.length > 0) {
      throw new Error("This email is already in use. Please use a different email.");
    }

    // 4. Check dummy phone email (your original check)
    const dummyEmail = `${cleanPhone}@firm.altrange.internal`;
    const dummyMethods = await fetchSignInMethodsForEmail(auth, dummyEmail);
    if (dummyMethods.length > 0) {
      throw new Error("This phone number is already linked to a firm.");
    }

    // 5. Generate password & create user
    const dummyPwd = Math.random().toString(36).slice(-8) + "Xy1!"; // ← can be shorter
    const cred = await createUserWithEmailAndPassword(auth, dummyEmail, dummyPwd);
    const firmUid = cred.user.uid;

      // Save firm profile
      await set(ref(db, `profileDashboard/${firmUid}`), {
        type: "firm",
        parentUid: currentAccount.uid,
        username: form.username.trim(),
        company_name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        gst: form.gst || null,
        pan: form.pan || null,
        createdAt: new Date().toISOString(),
      });

      // Link firm under main account, including pwd for deletion
      await set(ref(db, `profileDashboard/${currentAccount.uid}/firms/${firmUid}`), {
        uid: firmUid,
        company_name: form.name.trim(),
        pwd: dummyPwd, // Store pwd securely (assume DB rules protect it)
      });

      setMsg("Firm created successfully!");
      setMsgType("success");
      await reload();
      setTimeout(() => navigate("/dashboard"), 1000);
    } catch (err) {
    console.error(err);
    let msg = err.message || "Failed to create firm.";

    // Optional: nicer messages
    if (msg.includes("username is already taken")) {
      // already good
    } else if (err.code === "auth/email-already-in-use") {
      msg = "This email is already registered.";
    }

    setMsg(msg);
    setMsgType("error");
  } finally {
    setLoading(false);
  }
};

 const handleDelete = async (firm) => {
    if (!window.confirm(`Permanently delete "${firm.company_name}"?\n\nThis will delete ALL data (files, chats, orders, etc.) and the account.\nThis action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    setMsg("");

    try {
      const firmUid = firm.uid;

      // 1. Trigger self-delete for Auth user (no password needed)
      await set(ref(db, `profileDashboard/${firmUid}/deleteMe`), {
        triggeredBy: currentAccount.uid,
        at: new Date().toISOString(),
      });

      // 2. Delete ALL Realtime DB nodes
      const dbNodes = [
        "issues", "machineryOrder", "needHelp", "Finance", "negotiations", "new-code",
        "newProducts", "notes", "offers", "orders", "priceInformation", "profileDashboard",
        "queries", "quoteRequests", "rates", "rateRequest", "refunds", "regions",
        "steelOrder", "supplierContacts", "supplier_notifications", "transport_goods",
        "transport_goods_replies", "trendingProducts", "userBrandAlternatives",
        "userComparisonData", "user_notifications", "userchatbox", "groupChats",
        "ActivePO", "accessPermissions", "alt_alarm", "broadcasts", "feedback", "loanRequest"
      ];

      await Promise.all(dbNodes.map(node =>
        remove(ref(db, `${node}/${firmUid}`)).catch(() => {})
      ));
      await remove(ref(db, `profileDashboard/${firmUid}`));

      // 3. Delete ALL Storage folders
      const storage = getStorage();
      const storagePaths = [
        `Finance/${firmUid}`, `POFiles/${firmUid}`, `PaymentFiles/${firmUid}`,
        `chatDocuments/${firmUid}`, `chatFiles/${firmUid}`, `mtc/${firmUid}`,
        `pendingProducts/${firmUid}`, `refunds/${firmUid}`, `user-statements/${firmUid}`,
        `userDocuments/${firmUid}`
      ];

      await Promise.all(storagePaths.map(async (path) => {
        try {
          const folderRef = storageRef(storage, path);
          const list = await listAll(folderRef);
          await Promise.all(list.items.map(deleteObject));
          await Promise.all(list.prefixes.map(sub => listAll(sub).then(r => Promise.all(r.items.map(deleteObject)))));
        } catch (err) { /* ignore not found */ }
      }));

      // 4. Remove from main account
      await remove(ref(db, `profileDashboard/${currentAccount.uid}/firms/${firmUid}`));

      // 5. Update UI
      setFirms(prev => prev.filter(f => f.uid !== firmUid));
      setMsg("Firm deleted permanently!");
      setMsgType("success");
      await reload();
    } catch (err) {
      console.error(err);
      setMsg("Firm data deleted. Auth account will be removed automatically soon.");
      setMsgType("warning");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-firm-page-wrapper">
      <div className="add-firm-card">
        {/* Header */}
        <div className="add-firm-header">
          <button onClick={() => navigate(-1)} className="back-btn">
            <FiArrowLeft size={20} />
          </button>
          <h1>Add New Firm</h1>
          <button onClick={() => setShowInfo(true)} className="info-btn">
            <FaInfoCircle size={20} />
          </button>
        </div>
        

        {/* Alert */}
        <div className="add-firm-card-content">
         

          {/* Existing Firms Section */}
          {currentAccount?.type === "main" && (
            <div className="existing-firms">
              <h2 className="text-center">Existing Firms</h2>
              {firms.length > 0 ? (
                <div className="firm-list">
                  {firms.map((firm) => (
                    <div key={firm.uid} className="firm-card">
                      <h3>{firm.company_name}</h3>
                      <p><strong>Username:</strong> {firm.username}</p>
                      <p><strong>Phone:</strong> {firm.phone}</p>
                      <p><strong>Email:</strong> {firm.email || "N/A"}</p>
                      <p><strong>GST:</strong> {firm.gst || "N/A"}</p>
                      <p><strong>PAN:</strong> {firm.pan || "N/A"}</p>
                      <button
                        onClick={() => handleDelete(firm)}
                        disabled={loading}
                        className="btn-delete"
                      >
                        <FiTrash2 /> Delete
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-firms">No existing firms yet.</p>
              )}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="add-firm-form">
            <div className="form-grid">
              <div className="input-group firm-group">
                <label>
                  Username <span className="required">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  placeholder="e.g. xyz_traders"
                />
              </div>

              <div className="input-group firm-group">
                <label>
                  Firm Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="XYZ Traders Pvt Ltd"
                />
              </div>

              <div className="input-group firm-group">
                <label>
                  Phone Number <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+91 98765 43210"
                  maxLength="15"
                />
              </div>

              <div className="input-group firm-group">
                <label>Email  <span className="required">*</span></label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="firm@example.com"
                />
              </div>

              <div className="input-group firm-group">
                <label>GST Number (Optional)</label>
                <input
                  type="text"
                  value={form.gst}
                  onChange={(e) => handleGST(e.target.value)}
                  placeholder="22AAAAA0000A1Z5"
                  maxLength="15"
                  style={{ textTransform: "uppercase" }}
                />
              </div>

              <div className="input-group firm-group">
                <label>PAN (Auto-filled)</label>
                <input
                  type="text"
                  value={form.pan}
                  readOnly
                  placeholder="Will be auto-filled from GST"
                  className="readonly-input"
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" disabled={loading || currentAccount?.type !== "main"} className="btn-primary">
                {loading ? (
                  <>
                    <FiLoader className="spin" />
                    Creating Firm...
                  </>
                ) : (
                  "Create Firm"
                )}
              </button>
              <button type="button" onClick={() => navigate(-1)} className="btn-secondary">
                Cancel
              </button>
              
            </div>
             {msg && (
            <div className={`alert alert-${msgType}`}>
              {msgType === "success" ? <FiCheckCircle /> : <FiAlertCircle />}
              <span>{msg}</span>
            </div>
          )}
          </form>

          <div className="info-note">
           
          </div>
        </div>
      </div>

      {/* Info Modal */}
      {showInfo && (
        <div className="modal-overlay" onClick={() => setShowInfo(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>About the Add Firm </h2>
            <p>
              This page allows you (as the main account holder) to create and manage sub-accounts called "Firms." Firms are linked to your main account and can perform tasks like managing orders, stocks, viewing and downloading documents etc... However, only the main account can create or delete firms.
            </p>
            <p>
              <strong>How to use:</strong>
              <br />
              - Fill in the form details to add a new firm. Username, Firm Name,Email, and Phone Number are required.
              <br />
              - GST is optional, and PAN will auto-fill from GST if provided.
              <br />
              - View and delete existing firms in the section above the form.
            </p>
            <p>
              <strong>Role of Firms:</strong> Firms act as independent entities under your control, ideal for managing multiple businesses or branches without full admin access.
            </p>
            <button onClick={() => setShowInfo(false)} className="btn-secondary">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}